import React, { useMemo } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Box,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Button,
    Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet';
import { docsRoutes } from './DocsRoutes.Component';

import { fromMarkdown } from 'mdast-util-from-markdown';
import { toString } from 'mdast-util-to-string';
import GithubSlugger from 'github-slugger';
import DocsSearch from './DocSearch.Component';

/* -------------------- TOC HEADING EXTRACTION -------------------- */
export const extractHeadings = (markdown = '') => {
    if (!markdown) return [];

    const tree = fromMarkdown(markdown);
    const slugger = new GithubSlugger();
    const headings = [];

    tree.children.forEach(node => {
        if (node.type === 'heading') {
            const text = toString(node);
            headings.push({
                level: node.depth,
                text,
                id: slugger.slug(text), // matches rehype-slug exactly
            });
        }
    });

    return headings;
};

/* -------------------- RIGHT TOC -------------------- */
const DocsTOC = ({ headings }) => {
    if (!headings?.length) return null;

    return (
        <Box
            sx={{
                width: 260,
                pl: 2,
                pr: 2,
                borderLeft: '1px solid #e0e0e0',
                position: 'sticky',
                height: 'calc(100vh - 100px)',
                overflowY: 'auto',
                paddingTop: '30px',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <DocsSearch />
                <Typography variant="subtitle2" gutterBottom>
                    On this page
                </Typography>
            </Box>

            {headings.map(h => (
                <Box
                    key={h.id}
                    sx={{
                        cursor: 'pointer',
                        pl: (h.level - 1) * 2,
                        py: 0.5,
                        fontSize: 13,
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' },
                    }}
                    onClick={() =>
                        document.getElementById(h.id)?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        })
                    }
                >
                    {h.text}
                </Box>
            ))}
        </Box>
    );
};

/* -------------------- HELPERS (RECURSIVE) -------------------- */

// Flatten only LEAF docs for prev/next
const flattenDocsRoutes = (routes, parentLabel = null) => {
    const result = [];

    routes.forEach(route => {
        if (route.children?.length) {
            result.push(...flattenDocsRoutes(route.children, route.label));
        } else if (route.path && route.component) {
            result.push({
                ...route,
                groupLabel: parentLabel,
            });
        }
    });

    return result;
};

// Find current doc by path (recursive)
const findDocByPath = (routes, path) => {
    for (const route of routes) {
        if (route.children?.length) {
            const found = findDocByPath(route.children, path);
            if (found) return found;
        } else if (route.path === path) {
            return route;
        }
    }
    return null;
};

// Render sidebar tree (recursive)
const renderTree = (nodes, navigate) =>
    nodes.map(node => (
        <TreeItem
            key={node.label}
            itemId={node.id}
            label={node.label}
            onClick={
                node.path
                    ? () => navigate(`/docs/${node.path}`)
                    : undefined
            }
        >
            {node.children?.length && renderTree(node.children, navigate)}
        </TreeItem>
    ));

// Render routes (recursive)
const renderRoutes = routes =>
    routes.flatMap(route => {
        if (route.children?.length) {
            return renderRoutes(route.children);
        }

        if (!route.path || !route.component) return [];

        return (
            <Route
                key={route.path}
                path={route.path}
                element={<route.component />}
            />
        );
    });

/* -------------------- MAIN DOCS COMPONENT -------------------- */
const Docs = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

    const flatDocs = useMemo(
        () => flattenDocsRoutes(docsRoutes),
        []
    );

    const currentPath = location.pathname.replace('/docs/', '');

    const currentDoc = useMemo(
        () => findDocByPath(docsRoutes, currentPath),
        [currentPath]
    );

    const tocHeadings = useMemo(
        () => extractHeadings(currentDoc?.content),
        [currentDoc]
    );

    const currentIndex = flatDocs.findIndex(d =>
        location.pathname.endsWith(d.path)
    );

    const prevDoc = currentIndex > 0 ? flatDocs[currentIndex - 1] : null;
    const nextDoc = currentIndex < flatDocs.length - 1 ? flatDocs[currentIndex + 1] : null;

    return (
        <>
            <Helmet>
                <title>Docs - HawkClient</title>
                <meta name="description" content="HawkClient documentation" />
            </Helmet>

            <Box
                sx={{
                    display: 'flex',
                    height: 'calc(100vh - 64px)',
                    flexDirection: isMediumScreen ? 'column' : 'row',
                }}
            >
                {/* LEFT SIDEBAR */}
                {isMediumScreen ? (
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            Docs Menu
                        </AccordionSummary>
                        <AccordionDetails>
                            <SimpleTreeView
                                slots={{
                                    expandIcon: ChevronRight,
                                    collapseIcon: ExpandMore,
                                }}
                                defaultExpandedItems={['Introduction']}
                            >
                                {renderTree(docsRoutes, navigate)}
                            </SimpleTreeView>
                        </AccordionDetails>
                    </Accordion>
                ) : (
                    <Box
                        sx={{
                            width: 300,
                            borderRight: '1px solid #ddd',
                            p: 2,
                            overflowY: 'auto',
                        }}
                    >
                        <SimpleTreeView
                            slots={{
                                expandIcon: ChevronRight,
                                collapseIcon: ExpandMore,
                            }}
                            defaultExpandedItems={['Introduction']}
                        >
                            {renderTree(docsRoutes, navigate)}
                        </SimpleTreeView>
                    </Box>
                )}

                {/* CONTENT */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Routes>
                            <Route
                                path=""
                                element={<Navigate to="introduction/about" replace />}
                            />
                            {renderRoutes(docsRoutes)}
                        </Routes>
                    </Box>

                    {/* PREV / NEXT */}
                    <Box
                        sx={{
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            p: 2,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between">
                            <Button
                                disabled={!prevDoc}
                                onClick={() => navigate(`/docs/${prevDoc.path}`)}
                            >
                                ← {prevDoc?.label}
                            </Button>

                            <Button
                                disabled={!nextDoc}
                                onClick={() => navigate(`/docs/${nextDoc.path}`)}
                            >
                                {nextDoc?.label} →
                            </Button>
                        </Stack>
                    </Box>
                </Box>

                {/* RIGHT TOC */}
                {!isMediumScreen && <DocsTOC headings={tocHeadings} />}
            </Box>
        </>
    );
};

export default Docs;
